import React from 'react';
import { useSelector } from 'react-redux';
import { graphTypeEnums, goalGraphTypeEnums, goalGraphValueModeEnums, sessionGroupByEnums } from '../../attributes/constnats';
import { FormLineChart, FormBarChart } from '../../../fragments/formGraphs';
import { useFormBuilder } from '../../formContext';
import { goalDataTypeEnums } from 'utils';

const GET_DATA_ACTION_TYPE = 'GET_GOAL_CLINICAL_DATA';

// Graph data arrives grouped from the API — `@therapylake/package/clinical/graph`, the same code the PDF uses —
// so this component only maps it onto the chart: no bucketing or averaging here.
const SESSION_GROUP_BY_KEY = {
    [sessionGroupByEnums.DAY]: 'day',
    [sessionGroupByEnums.WEEK]: 'week',
    [sessionGroupByEnums.MONTH]: 'month',
};

const getGoalGraphPoints = (goalGraph) => {
    if (Array.isArray(goalGraph?.data)) return goalGraph.data;
    if (Array.isArray(goalGraph)) return goalGraph;
    return [];
};

export const GraphCharts = ({ item, sectionIndex, goalId, targetId }) => {
    const goalClinicalData = useSelector((state) => state.goals.goalClinicalData);
    const goalTargetClinicalData = useSelector((state) => state.goals.goalTargetClinicalData);
    const { formList } = useFormBuilder();
    const allSelectedGoals = (formList?.sections || []).flatMap((s) => s?.selectedGoals || []);
    const goal = allSelectedGoals.find((g) => g.id === goalId || g._id === goalId);
    const isCumulative = item?.defaultDateType === goalGraphTypeEnums.CUMULATIVE;
    const isTargetOverview = item?.defaultDateType === goalGraphTypeEnums.TARGET_OVERVIEW;
    const isCountMode = isCumulative && item?.properties?.valueMode === goalGraphValueModeEnums.COUNT;
    const showMastery = !!item?.properties?.showMastery;
    const showTrendLine = !!item?.properties?.showTrendLine;
    const showAverageLine = !!item?.properties?.showAverageLine;
    const showGoalEvent = !!item?.properties?.showGoalEvent;
    const showClientEvent = !!item?.properties?.showClientEvent;
    const showTargetEvent = !!item?.properties?.showTargetEvent;
    const showBaseline = !!item?.properties?.showBaseline;

    let categories = [];
    let chartSeries = [];
    let baselinePhase;
    let clientEvents = [];
    let goalEvents = [];
    let targetEvents = [];
    let goalMastery = [];
    let chartDataType = goalDataTypeEnums.PERCENT;
    let totalTargets = 0;

    if (isCumulative) {
        const goalData = goalId && goalClinicalData?.[goalId] ? goalClinicalData[goalId] : {};
        const currentGoalGraph = goalData?.graph;
        const currentGoalData = getGoalGraphPoints(currentGoalGraph);
        chartDataType = isCountMode ? null : goalDataTypeEnums.PERCENT;
        // The API returns one point per bucket of the report period.
        categories = currentGoalData.map((point) => point?.date).filter(Boolean);
        totalTargets = goalData?.totalTargets ?? goal?.targets?.length ?? 0;

        if (currentGoalData.length > 0) {
            const dateValueMap = {};
            currentGoalData.forEach((d) => {
                if (!d?.date) return;
                dateValueMap[d.date] = isCountMode ? (d.cumulativeCount ?? null) : (d.masteredPercent ?? d.cumulative ?? d.value ?? null);
            });

            chartSeries = [
                {
                    name: goal?.title || goal?.name || goalId || 'Progress',
                    data: categories.map((bucketKey) => (dateValueMap[bucketKey] !== undefined ? dateValueMap[bucketKey] : null)),
                },
            ];
        }

        clientEvents = Array.isArray(goalData?.clientEvents) ? goalData.clientEvents : [];
        goalEvents = Array.isArray(goalData?.goalEvents) ? goalData.goalEvents : [];

        // goalMastery is an object with count (a percentage), convert to array for the chart.
        // In Count mode the threshold becomes a number of targets, so it needs the goal's target count.
        const masteryObj = goalData?.goalMastery;
        if (masteryObj?.count === undefined) {
            goalMastery = [];
        } else if (isCountMode) {
            goalMastery = totalTargets ? [{ count: Math.ceil((totalTargets * masteryObj.count) / 100) }] : [];
        } else {
            goalMastery = [masteryObj];
        }
    }

    if (isTargetOverview) {
        const goalEntry = goalId && goalTargetClinicalData?.[goalId] ? goalTargetClinicalData[goalId] : {};
        const targetsMap = goalEntry?.targets || {};
        // "Group Sessions By" picks one of the API's groupings; AUTO uses the one the API resolved for the period.
        const groupBy = SESSION_GROUP_BY_KEY[item?.properties?.sessionGroupBy] || goalEntry?.buckets?.auto || 'month';
        categories = goalEntry?.buckets?.[groupBy] || [];

        // If targetId is provided, filter to only that target's data
        let targetEntries;
        if (targetId) {
            const singleTarget = targetsMap[targetId];
            targetEntries = singleTarget ? [{ id: targetId, ...singleTarget }] : [];
        } else {
            targetEntries = Object.entries(targetsMap).map(([id, target]) => ({ id, ...target }));
        }
        chartDataType = targetEntries[0]?.graphData?.dataType || goal?.dataType || goalDataTypeEnums.PERCENT;

        // One series per target; the API sends only the buckets that have data.
        chartSeries = targetEntries.map((target) => {
            const bucketValueMap = {};
            (target?.graphData?.progress?.[groupBy] || []).forEach((point) => {
                if (point?.date) bucketValueMap[point.date] = point.value ?? null;
            });

            return {
                name: target?.graphData?.name || 'Target',
                data: categories.map((bucketKey) => (bucketValueMap[bucketKey] !== undefined ? bucketValueMap[bucketKey] : null)),
            };
        });

        // TARGET "Show Target Baseline" (one target): its ACTIVE baseline as its own phase, one point per session.
        if (showBaseline && targetId) {
            baselinePhase = targetEntries[0]?.graphData?.baselinePhase || [];
        }

        clientEvents = Array.isArray(goalEntry?.clientEvents) ? goalEntry.clientEvents : [];
        goalEvents = Array.isArray(goalEntry?.goalEvents) ? goalEntry.goalEvents : [];

        // Target events are inside each target (targets[id].events), collect from matching entries
        targetEvents = targetEntries.flatMap((target) => (Array.isArray(target?.events) ? target.events : []));

        if (targetId) {
            const singleTarget = targetsMap[targetId];
            const mastery = singleTarget?.mastery;
            goalMastery = mastery?.count !== undefined ? [mastery] : [];
        } else {
            const goalMasteryObj = goalEntry?.goalMastery;
            goalMastery = goalMasteryObj?.count !== undefined ? [goalMasteryObj] : [];
        }
    }

    const timeGroupBy = isCumulative
        ? goalClinicalData?.[goalId]?.graph?.groupBy || 'month'
        : SESSION_GROUP_BY_KEY[item?.properties?.sessionGroupBy] || goalTargetClinicalData?.[goalId]?.buckets?.auto || 'month';

    return (
        <div>
            <div>
                {item?.properties?.graphType === graphTypeEnums.LINE && (
                    <FormLineChart
                        item={item}
                        categories={categories}
                        series={chartSeries}
                        actionType={GET_DATA_ACTION_TYPE}
                        timeGroupBy={timeGroupBy}
                        dataType={chartDataType}
                        isPercent={(isCumulative && !isCountMode) || isTargetOverview}
                        countMax={isCountMode && totalTargets ? totalTargets : undefined}
                        clientEvents={showClientEvent ? clientEvents : []}
                        goalEvents={showGoalEvent ? goalEvents : []}
                        targetEvents={showTargetEvent ? targetEvents : []}
                        goalMastery={showMastery ? goalMastery : []}
                        masteryLabel={isTargetOverview ? 'Mastery' : 'Goal Mastery'}
                        seriesConfig={item?.properties?.chartSettings?.seriesConfig || []}
                        showTrendLine={showTrendLine}
                        showAverageLine={showAverageLine}
                        baselinePhase={baselinePhase}
                    />
                )}
                {item?.properties?.graphType === graphTypeEnums.BAR && (
                    <FormBarChart
                        item={item}
                        categories={categories}
                        series={chartSeries}
                        actionType={GET_DATA_ACTION_TYPE}
                        timeGroupBy={timeGroupBy}
                        dataType={chartDataType}
                        isPercent={(isCumulative && !isCountMode) || isTargetOverview}
                        countMax={isCountMode && totalTargets ? totalTargets : undefined}
                        clientEvents={showClientEvent ? clientEvents : []}
                        goalEvents={showGoalEvent ? goalEvents : []}
                        targetEvents={showTargetEvent ? targetEvents : []}
                        goalMastery={showMastery ? goalMastery : []}
                        masteryLabel={isTargetOverview ? 'Mastery' : 'Goal Mastery'}
                        seriesConfig={item?.properties?.chartSettings?.seriesConfig || []}
                        showTrendLine={showTrendLine}
                        showAverageLine={showAverageLine}
                        baselinePhase={baselinePhase}
                    />
                )}
            </div>
        </div>
    );
};
